import { View, ImageBackground, Pressable, Linking } from "react-native";
import React, { useMemo } from "react";
import { PlanPackagePromotionDetails } from "features/types";
import { Swiper, SwiperSlide } from "components/Swiper";
import WhiteText from "components/WhiteText";
import { useFetchEventAvailablitiesQuery } from "queries/ticket.queries";
import { useAccount } from "hooks/useAccount";
import { useAppSelector } from "hooks/useAppSelector";
import {
  toAddNewPlanWithPlanPackageId,
  toScehdule,
  toSeatSelection,
} from "utils/navigationUtils";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  setEventFilters,
  setSelectedEvent,
  setSelectedSubscription,
} from "features/slices/reservationFlowSlice";
import useCurrentUser from "hooks/useCurrentUser";
import { getUniqSubscriptions } from "utils/subscriptionUtils";

const PromotionAds = ({
  promotions,
}: {
  promotions: PlanPackagePromotionDetails[];
}) => {
  const { account } = useAccount();
  const { currentMember } = useCurrentUser();
  const dispatch = useAppDispatch();
  const { selectedSubscription } = useAppSelector(
    (state) => state.reservationFlow
  );

  const { data: schedule } = useFetchEventAvailablitiesQuery({
    accountId: account!.id,
    planPackageId: selectedSubscription?.planPackage.id,
  });

  const toSeatSelectionPage = (promotion: PlanPackagePromotionDetails) => {
    const selectedEvent = schedule?.find(
      (eventAvailablity) => eventAvailablity.eventId === promotion.event?.id
    );
    if (selectedEvent) {
      dispatch(setSelectedEvent(selectedEvent));
      toSeatSelection();
    }
  };

  const handleImagePress = (promotion: PlanPackagePromotionDetails) => {
    switch (promotion.destination) {
      case "EXTERNAL_URL": {
        Linking.openURL(promotion.destinationUrl);
        break;
      }
      case "RESERVATION": {
        toSeatSelectionPage(promotion);
        break;
      }
      case "SCHEDULE": {
        const subscriptionAssigned = getUniqSubscriptions(
          account!.subscriptions
        ).find((sub) => sub.planPackage.id === promotion.planPackageId);

        dispatch(setSelectedSubscription(subscriptionAssigned!));
        dispatch(
          setEventFilters({
            months: promotion.months?.split(",") || [],
            days: promotion.days?.split(",") || [],
            eventTypes: promotion.eventTypes?.split(",") || [],
          })
        );
        toScehdule();
        break;
      }
      case "ADD_PLAN": {
        toAddNewPlanWithPlanPackageId(
          String(currentMember?.id),
          String(promotion.addNewPlanId)
        );
        break;
      }

      default: {
        break;
      }
    }
  };

  const activePromotions = useMemo(() => {
    if (!account || !promotions) return [];
    // Only display promotions that the user has a subscription to the plan package
    return promotions.filter((promotion) => {
      return account.subscriptions.some(
        (sub) => !sub.expired && sub.planPackage.id === promotion.planPackageId
      );
    });
  }, [promotions, account]);

  return (
    <View style={{ overflow: "hidden" }}>
      <Swiper
        style={{ width: "100%" }}
        loop={activePromotions.length > 1}
        autoplay={{
          delay: 4500,
          disableOnInteraction: false,
        }}
      >
        {activePromotions.map((promotion) => (
          <SwiperSlide key={promotion.id} style={{ width: "100%" }}>
            <Pressable onPress={() => handleImagePress(promotion)}>
              <ImageBackground
                source={{ uri: promotion.imageUrl }}
                style={{ aspectRatio: 21 / 9, position: "relative" }}
                imageStyle={{ borderRadius: 15 }}
              >
                {promotion.title && (
                  <View className="absolute bottom-0 h-14 p-2 w-full bg-black rounded-b-lg bg-opacity-50">
                    <WhiteText>{promotion.title}</WhiteText>
                  </View>
                )}
              </ImageBackground>
            </Pressable>
          </SwiperSlide>
        ))}
      </Swiper>
    </View>
  );
};

export default PromotionAds;
